import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IFiltrosProjeto,
  IRecorte,
  IAnaliseRecorte,
  IRecorteExtracao,
} from "../../../models";
import { IFormError, IFormProps } from "../../utils";

const createRecorteExtracaoFormFieldsInitialState: IRecorte = {
  Nome: "",
  Filtros: null,
  Quantidade: 0,
};

const editRecorteExtracaoFormFieldsInitialState: IRecorte = {
  Nome: "",
  Filtros: null,
  Quantidade: 0,
};

export const validateCreateRecorteExtracaoForm = (
  fields: IRecorte
): IFormError<keyof IRecorte>[] => {
  const errors: IFormError<keyof IRecorte>[] = [];

  if (!fields.Nome) {
    errors.push({
      key: "Nome",
      message: "É obrigatório inserir um nome para o recorte da extração",
    });
  } else {
    if (
      fields.Nome.length === 0 ||
      fields.Nome.replace("  ", " ").trim().length === 0
    ) {
      errors.push({
        key: "Nome",
        message: "É obrigatório inserir um nome para o recorte da extração",
      });
    }
  }

  if (!fields.Quantidade) {
    errors.push({
      key: "Quantidade",
      message: "É obrigatório inserir a quantidade de registros",
    });
  }

  if (fields.Quantidade < 1) {
    errors.push({
      key: "Quantidade",
      message: "Insira uma quantidade maior que 0",
    });
  }

  return errors;
};

export const validateEditRecorteExtracaoForm = (
  fields: IRecorte
): IFormError<keyof IRecorte>[] => {
  const errors: IFormError<keyof IRecorte>[] = [];

  if (!fields.Nome) {
    errors.push({
      key: "Nome",
      message: "É obrigatório inserir um nome para o recorte da extração",
    });
  } else {
    if (
      fields.Nome.length === 0 ||
      fields.Nome.replace("  ", " ").trim().length === 0
    ) {
      errors.push({
        key: "Nome",
        message: "É obrigatório inserir um nome para o recorte da extração",
      });
    }
  }

  if (!fields.Quantidade) {
    errors.push({
      key: "Quantidade",
      message: "É obrigatório inserir a quantidade de registros",
    });
  }

  if (fields.Quantidade < 1) {
    errors.push({
      key: "Quantidade",
      message: "Insira uma quantidade maior que 0",
    });
  }
  return errors;
};

function addFilterProp<T extends keyof IFiltrosProjeto>(
  filtros: IFiltrosProjeto,
  key: T,
  value: IFiltrosProjeto[T]
): void {
  filtros[key] = value;
}

function removeFilterProp<T extends keyof IFiltrosProjeto>(
  filtros: IFiltrosProjeto,
  key: T
): void {
  delete filtros[key];
}

interface IRecorteExtracaoState {
  recorteExtracao: IRecorteExtracao;
  recortesExtracao: IRecorteExtracao[];
  recorteAtual: IRecorteExtracao;
  recorteErrors?: IAnaliseRecorte[];
  recortesDisponiveis?: IAnaliseRecorte[];
  createRecorteExtracaoForm: IFormProps<IRecorte>;
  editRecorteExtracaoForm: IFormProps<IRecorte>;
  openForm: boolean;
  createOpenForm: boolean;
  editOpenForm: boolean;
  selectedFilter?: keyof IFiltrosProjeto | null;
  downloadLocked: boolean;
  recorteEditando?: string;
  indexRecorte?: number;
}

const initialState: IRecorteExtracaoState = {
  recorteExtracao: {
    IdVelesGeoRecorteExtracao: 0,
    IdVelesGeoProjeto: 0,
    Recortes: [],
    Inclusao: new Date(),
    Atualizacao: null,
  },

  recortesExtracao: [],

  recorteAtual: {
    IdVelesGeoRecorteExtracao: 0,
    IdVelesGeoProjeto: 0,
    Recortes: [],
    Inclusao: new Date(),
    Atualizacao: null,
  },

  createRecorteExtracaoForm: {
    errors: [],
    fields: createRecorteExtracaoFormFieldsInitialState,
    touched: false,
    delivered: false,
  },

  editRecorteExtracaoForm: {
    errors: [],
    fields: editRecorteExtracaoFormFieldsInitialState,
    touched: false,
    delivered: false,
  },

  openForm: false,
  createOpenForm: false,
  editOpenForm: false,
  downloadLocked: true,
  recorteEditando: undefined,
};

const recorteExtracaoSlice = createSlice({
  name: "recorteExtracao",
  initialState,
  reducers: {
    setRecorteExtracaoState(state, action: PayloadAction<IRecorteExtracao>) {

      state.recorteExtracao = action.payload;
    },
    setRecortesExtracaoState(state, action: PayloadAction<IRecorteExtracao[]>) {
      state.recortesExtracao = action.payload;
    },
    resetRecorteExtracaoState(state) {
      state.createRecorteExtracaoForm = initialState.createRecorteExtracaoForm;
      state.downloadLocked = initialState.downloadLocked;
      state.openForm = initialState.openForm;
      state.recorteErrors = initialState.recorteErrors;
      state.recorteExtracao = initialState.recorteExtracao;
      state.recortesDisponiveis = initialState.recortesDisponiveis;
      state.selectedFilter = initialState.selectedFilter;
    },
    addRecorteState(
      state,
      action: PayloadAction<{
        recorte: {
          Nome: string;
          Filtros: IFiltrosProjeto | null;
          Quantidade: number;
        };
      }>
    ) {
      state.recorteExtracao.Recortes.push(action.payload.recorte);
    },
    removeRecorteState(
      state,
      action: PayloadAction<{
        recorte: {
          Nome: string;
          Filtros: IFiltrosProjeto | null;
          Quantidade: number;
        };
      }>
    ) {
      const recorteExtracao = action.payload.recorte;

      const findRecorteExtracao = state.recorteExtracao.Recortes.find(
        (recorte) => recorte.Nome === recorteExtracao.Nome
      );

      if (findRecorteExtracao) {
        const index =
          state.recorteExtracao.Recortes.indexOf(findRecorteExtracao);
        state.recorteExtracao.Recortes.splice(index, 1);
      }

      if (state.recorteExtracao.Recortes.length === 0) {
        state.recorteExtracao.Recortes = initialState.recorteExtracao.Recortes;
      }
    },
    // Create RecorteExtracao Actions
    setCreateRecorteFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.createRecorteExtracaoForm.fields.Nome = action.payload.nome;
    },
    setCreateRecorteFormFiltrosState(
      state,
      action: PayloadAction<{
        key: keyof IFiltrosProjeto;
        value: any;
      }>
    ) {
      const { key, value } = action.payload;

      if (!state.createRecorteExtracaoForm.fields.Filtros) {
        state.createRecorteExtracaoForm.fields.Filtros = {};
      }

      addFilterProp(state.createRecorteExtracaoForm.fields.Filtros, key, value);
    },

    // Edit RecorteExtracao Actions
    setEditRecorteFormNomeState(
      state,
      action: PayloadAction<{ nome: string }>
    ) {
      state.editRecorteExtracaoForm.fields.Nome = action.payload.nome;
    },

    setEditRecorteFormFiltrosState(
      state,
      action: PayloadAction<{
        key: keyof IFiltrosProjeto;
        value: any;
      }>
    ) {
      const { key, value } = action.payload;

      if (!state.editRecorteExtracaoForm.fields.Filtros) {
        state.editRecorteExtracaoForm.fields.Filtros = {};
      }

      addFilterProp(state.editRecorteExtracaoForm.fields.Filtros, key, value);
    },

    removeCreateRecorteFormFiltrosState(
      state,
      action: PayloadAction<{
        key: keyof IFiltrosProjeto;
      }>
    ) {
      const { key } = action.payload;

      if (state.createRecorteExtracaoForm.fields.Filtros) {
        removeFilterProp(state.createRecorteExtracaoForm.fields.Filtros, key);
      }
    },
    removeEditRecorteFormFiltrosState(
      state,
      action: PayloadAction<{
        key: keyof IFiltrosProjeto;
      }>
    ) {
      const { key } = action.payload;

      if (state.editRecorteExtracaoForm.fields.Filtros) {
        removeFilterProp(state.editRecorteExtracaoForm.fields.Filtros, key);
      }
    },
    setCreateRecorteFormQuantidadeState(
      state,
      action: PayloadAction<{ quantidade: number }>
    ) {
      state.createRecorteExtracaoForm.fields.Quantidade =
        action.payload.quantidade;
    },

    setEditRecorteFormQuantidadeState(
      state,
      action: PayloadAction<{ quantidade: number }>
    ) {
      state.editRecorteExtracaoForm.fields.Quantidade =
        action.payload.quantidade;
    },

    setCreateRecorteFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.createRecorteExtracaoForm.touched = action.payload.touched;
    },
    setCreateRecorteFormErrorsState(
      state,
      action: PayloadAction<{ errors: IFormError<keyof IRecorte>[] }>
    ) {
      state.createRecorteExtracaoForm.errors = action.payload.errors;
    },
    setEditRecorteFormErrorsState(
      state,
      action: PayloadAction<{ errors: IFormError<keyof IRecorte>[] }>
    ) {
      state.editRecorteExtracaoForm.errors = action.payload.errors;
    },
    setCreateRecorteFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.createRecorteExtracaoForm.delivered = action.payload.delivered;
    },
    resetCreateRecorteFormState(state) {
      state.createRecorteExtracaoForm = initialState.createRecorteExtracaoForm;
    },
    resetEditRecorteFormState(state) {
      state.editRecorteExtracaoForm = initialState.editRecorteExtracaoForm;
    },
    setOpenFormCreateRecorteState(state, action: PayloadAction<boolean>) {
      state.openForm = action.payload;
      state.createOpenForm = action.payload;
    },
    setSelectedFilterState(
      state,
      action: PayloadAction<keyof IFiltrosProjeto>
    ) {
      state.selectedFilter = action.payload;
    },
    resetSelectedFilterState(state) {
      state.selectedFilter = initialState.selectedFilter;
    },
    setDownloadLockedState(state, action: PayloadAction<boolean>) {
      state.downloadLocked = action.payload;
    },
    setRecorteErrorsState(state, action: PayloadAction<IAnaliseRecorte[]>) {
      state.recorteErrors = action.payload;
    },
    setResetRecorteErrorsState(state) {
      state.recorteErrors = initialState.recorteErrors;
    },
    setRecortesDisponiveisState(
      state,
      action: PayloadAction<IAnaliseRecorte[]>
    ) {
      state.recortesDisponiveis = action.payload;
    },
    setResetRecortesDisponiveisState(state) {
      state.recortesDisponiveis = initialState.recortesDisponiveis;
    },
    setOpenFormEditRecorteState(state, action: PayloadAction<boolean>) {
      state.editOpenForm = action.payload;
      state.openForm = action.payload;
    },
    setEditRecorteState(state, action: PayloadAction<IRecorte>) {
      state.editRecorteExtracaoForm.fields = action.payload;
    },
    setEditRecorteFormTouchedState(
      state,
      action: PayloadAction<{ touched: boolean }>
    ) {
      state.editRecorteExtracaoForm.touched = action.payload.touched;
    },
    setEditRecorteFormDeliveredState(
      state,
      action: PayloadAction<{ delivered: boolean }>
    ) {
      state.editRecorteExtracaoForm.delivered = action.payload.delivered;
    },
    setEditRecorteFormState(
      state,
      action: PayloadAction<IFormProps<IRecorte>>
    ) {
      state.editRecorteExtracaoForm = action.payload;
    },
    setRecorteEditandoState(
      state,
      action: PayloadAction<string>
    ) {
      state.recorteEditando = action.payload;
    },
    resetRecorteEditandoState(state) {
      state.recorteEditando = initialState.recorteEditando;
    },

    setRecorteExtracaoAtualState(
      state,
      action: PayloadAction<IRecorteExtracao>
    ) {
      state.recorteAtual = action.payload;
    },

    setUpdateRecorteAtualState(
      state,
      action: PayloadAction<{ index: number; recorte: IRecorte }>
    ) {
      if (state.recorteExtracao.Recortes?.[action.payload.index]) {
        state.recorteExtracao.Recortes[action.payload.index] = {
          ...state.recorteExtracao.Recortes[action.payload.index],
          Nome: action.payload.recorte.Nome,
          Quantidade: action.payload.recorte.Quantidade,
          Filtros: action.payload.recorte.Filtros,
        };
      }
    },
    setIndexRecorteState(state, action: PayloadAction<number>) {
      state.indexRecorte = action.payload;
    },
    resetIndexRecorteState(state) {
      state.indexRecorte = 0;
    },

    syncFilterWithForm<T extends keyof IFiltrosProjeto>(
      state: IRecorteExtracaoState,
      action: PayloadAction<{
        filtro: T;
        value: IFiltrosProjeto[T];
        formType: 'create' | 'edit';
      }>
    ) {
      const { filtro, value, formType } = action.payload;

      const form = formType === 'create'
        ? state.createRecorteExtracaoForm
        : state.editRecorteExtracaoForm;

      if (!form.fields.Filtros) {
        form.fields.Filtros = {};
      }

      addFilterProp(form.fields.Filtros, filtro, value);
    },

    removeFilterFromForm<T extends keyof IFiltrosProjeto>(
      state : IRecorteExtracaoState,
      action: PayloadAction<{
        filtro: T;
        formType: 'create' | 'edit';
      }>
    ) {
      const { filtro, formType } = action.payload;

      const form = formType === 'create'
        ? state.createRecorteExtracaoForm
        : state.editRecorteExtracaoForm;

      if (form.fields.Filtros) {
        removeFilterProp(form.fields.Filtros, filtro);

        if (Object.keys(form.fields.Filtros).length === 0) {
          form.fields.Filtros = null;
        }
      }
    },
  },
});

export const {
  addRecorteState,
  removeRecorteState,
  resetCreateRecorteFormState,
  resetRecorteExtracaoState,
  setCreateRecorteFormDeliveredState,
  setCreateRecorteFormErrorsState,
  setCreateRecorteFormFiltrosState,
  setEditRecorteFormFiltrosState,
  removeCreateRecorteFormFiltrosState,
  removeEditRecorteFormFiltrosState,
  setCreateRecorteFormQuantidadeState,
  setEditRecorteFormQuantidadeState,
  setCreateRecorteFormNomeState,
  setCreateRecorteFormTouchedState,
  setEditRecorteFormNomeState,
  setRecorteExtracaoState,
  setRecortesExtracaoState,
  setOpenFormCreateRecorteState,
  resetSelectedFilterState,
  setSelectedFilterState,
  setDownloadLockedState,
  setRecorteErrorsState,
  setResetRecorteErrorsState,
  setRecortesDisponiveisState,
  setResetRecortesDisponiveisState,
  setEditRecorteFormState,
  setEditRecorteState,
  setOpenFormEditRecorteState,
  resetRecorteEditandoState,
  setEditRecorteFormErrorsState,
  setEditRecorteFormTouchedState,
  resetEditRecorteFormState,
  setRecorteExtracaoAtualState,
  setUpdateRecorteAtualState,
  setIndexRecorteState,
  resetIndexRecorteState,
  setRecorteEditandoState,
  syncFilterWithForm,
  removeFilterFromForm,
} = recorteExtracaoSlice.actions;

export const recorteExtracaoReducer = recorteExtracaoSlice.reducer;
